/*
  Lunya brand adapter: the visitor's recently viewed products, for the homepage "Styles Just For
  You" row that replaces the Glood recommendations app prod renders it through (see
  docs/superpowers/specs/2026-09-17-lunya-home-product-rows-design.md).

  The row is server-rendered from a real collection, so every shopper — no history, no storage, no
  JavaScript — gets a complete row. This file only reorders it: it holds the history the PDP
  records, and the rule that decides which fetched cards replace which of the rendered ones.

  Here that logic sits on its own, as static methods: none of them touches the DOM or the network,
  and none reads `window` except `storage()`, whose entire job is to reach `window.localStorage`
  without throwing. Keeping it separable is what lets the ordering rules — the part with the edge
  cases — be tested without a browser, and the tests reach them through `customElements.define`, so
  nothing here needs to be exported.

  Two elements, because the two halves of the feature live on different pages:

  - `<lunya-view-recorder>` on the PDP (sections/main-product.liquid) writes the view and does
    nothing else. It sits before `<product-form class="quickbuy-content">`, which is the only part
    of a PDP quick buy clones, so opening a card's quick buy never records a view for it.
  - `<lunya-recently-viewed>` IS the homepage row's grid. On connect it reads the history, and —
    once the row is near the viewport — asks the search route for exactly those products as cards,
    in ONE Section Rendering request. The response comes back in relevance order, not the order it
    was asked in (measured against the stage store), so the row's order comes from `compose` and
    the stored recency; the fetch only decides which of them still exist. The rendered cards it
    does not need are the backfill, moved rather than re-created, so they keep whatever state the
    theme's own scripts have already given them.

  Everything after the read is best effort. A failed request, an aborted one, a shop with no
  history and a shopper who declined preference cookies all end at the row the server rendered.
*/
(() => {
  /*
    No custom elements at all: nothing to do, and nothing lost — the row the server rendered is a
    complete one.

    The `get` guard is for a second evaluation in the same document, which a repeat `define` would
    answer with a NotSupportedError. Two `<script src>` tags for this asset both execute, and the
    homepage row and the PDP recorder each carry their own; so does a merchant who duplicates the
    row section in the theme editor. Quick buy injects the PDP's `script[src]` tags into any page a
    card is opened on (assets/main.js:3142-3149) — that path skips a src already in the document,
    but it is the same requirement the theme's own elements are written to, so this file meets it.
  */
  if (!window.customElements || customElements.get('lunya-recently-viewed')) return;

  /*
    Versioned: entries are `{ id: String, handle: String }`, newest first, and a later change to
    that shape becomes a new key rather than a migration of whatever is already sitting in a
    shopper's browser.
  */
  const STORAGE_KEY = 'lunya:recently-viewed:v1';
  /*
    Ten entries, which is also the most a row can show (`products_to_show` runs to 10) and the page
    size the fetched section paginates by. The depth is what lets the row still be filled after
    some of the stored products have been unpublished or hidden from search: every stored id is
    asked for, and `compose` truncates what comes back to the row's own length.
  */
  const HISTORY_CAP = 10;

  /*
    How far below the viewport the row asks for its cards, handed to Symmetry's own helper
    (`initLazyScript(element, callback, threshold = 500)`, assets/main.js:7). Measured on the stage
    store 2026-09-17: at the stock 500px a brisk scroll (2500px/s) fired the request with the row
    322px below the fold, and the swap landed with the row's top already at 820px of a 1000px
    viewport — i.e. on screen, which is the one thing the deferral is for.
  */
  const LAZY_THRESHOLD = 2000;

  /*
    The search route stamps its own analytics onto every link it renders — measured live on the
    stage store 2026-09-17:
    `href="/products/womens-washable-silk-tulip-back-set-deep-blue?_pos=1&_sid=142791cba&_ss=r"`,
    on all three links a card has (image, title, quick-buy toggle). Left on, a click from the
    personalised row would be logged as a search click-through for an `id:… OR id:…` query the
    shopper never made, and a fetched card's links would differ from a server-rendered one's.

    Shopify's storefront analytics params are underscore-prefixed (predictive search adds `_psq`
    and `_v`), so the rule is the prefix, not today's three names — a denylist rots the day a
    fourth appears. A param that merely contains an underscore (`selling_plan`) is the card's own.
  */
  const isTrackingParam = (name) => name.startsWith('_');

  /*
    `sections/lunya-recently-viewed-cards.liquid`, rendered standalone on the search route: the
    Section Rendering API addresses a section by filename, so this string is that filename and
    renaming the section without changing it fetches nothing. Its cards carry the marker div, which
    is what separates them from the rest of a search page's markup.
  */
  const CARDS_SECTION = 'lunya-recently-viewed-cards';
  const FETCHED_CARDS = '[data-lunya-recently-viewed-cards] > product-block';
  // The row's own cards: direct children only, so a `product-block` inside an open quick buy panel
  // cannot be mistaken for one of the row's.
  const ROW_CARDS = ':scope > product-block';
  /*
    The wrapper `snippets/image.liquid` puts the card's aspect ratio on, scoped to the product image
    so the 1:1 swatch thumbnails in the same card are left alone. The fetched section chooses its
    ratio from ITS OWN product set (`snippets/lunya-product-cards.liquid` takes the tallest or
    widest of them), so an incoming card can disagree with the row it is joining — which would move
    the page under the shopper at the moment of the swap.
  */
  const CARD_IMAGE_RATIO = '.product-block__image .img-ar';
  const ASPECT_RATIO = '--aspect-ratio';
  // Symmetry marks an open quick buy on the card (assets/main.js:3076).
  const OPEN_QUICK_BUY = '.product-block.expanded';

  // Function declaration, so the gate below can call it and it can reach the class defined later.
  // Never throws: a blocked or missing `removeItem` leaves the history unread, which is all the
  // gate needed anyway.
  function forgetHistory() {
    try {
      const storage = LunyaRecentlyViewed.storage();
      if (storage && typeof storage.removeItem === 'function') storage.removeItem(STORAGE_KEY);
    } catch (error) { /* nothing to do: the history simply stays unread */ }
  }

  /*
    Shared by both elements. Three reasons to do nothing at all:

    - The theme editor, where `Shopify.designMode` is true. A merchant arranging the homepage would
      otherwise have every PDP they opened in the preview recorded into their own browser, and then
      watch the row reorder itself around products they were only checking.
    - A shopper who declined preference cookies. "Recently viewed" is exactly the preferences
      category, and Shopify's Customer Privacy API answers for it.
    - A consent API that cannot answer. A throwing `preferencesProcessingAllowed` fails CLOSED:
      "we could not ask" is not permission, and an exception here would also escape into a
      `connectedCallback` and an IntersectionObserver callback where nothing is catching.

    The API only exists once the shop has loaded it, so its absence is not a refusal either: with
    no consent API present this gate is a no-op and the enhancement runs, which is the behaviour of
    a shop that has not configured consent tracking at all. `whenPersonalisationAllowed` below is
    what makes sure "absent" really means absent rather than "not here yet".

    Called at every decision point, never cached: a shopper can decline between a row connecting
    and its cards being asked for.
  */
  /*
    What the shop says about preferences consent, and nothing else — no side effects here.

    - 'granted'  the API answered `true`, or there is no consent API to ask (not configured).
    - 'refused'  the API answered `false`. The only answer that is a refusal.
    - 'unknown'  we could not ask: the theme editor, a call that threw, or an answer that is neither
                 boolean (code review of #29: `!== false` read undefined, null and 0 as consent).
                 Unknown blocks like a refusal does, but it is not one.
  */
  const consentState = () => {
    const shopify = window.Shopify;
    if (!shopify) return 'granted';
    if (shopify.designMode) return 'unknown';
    const privacy = shopify.customerPrivacy;
    if (!privacy || typeof privacy.preferencesProcessingAllowed !== 'function') return 'granted';
    try {
      const answer = privacy.preferencesProcessingAllowed();
      if (answer === true) return 'granted';
      if (answer === false) return 'refused';
    } catch (error) { /* falls through to unknown */ }
    return 'unknown';
  };

  /*
    The gate every decision point reads — and it ENFORCES as well as answers, which is why it is not
    a bare getter: a real refusal, and only that, also DELETES what was stored (Ryan, 2026-09-17).
    A shopper who withdraws preferences consent should not keep a browsing history in their browser
    because nothing happened to read it. 'unknown' deletes nothing: a failed read must never be what
    destroys a real record.
  */
  const personalisationAllowed = () => {
    const state = consentState();
    if (state === 'refused') forgetHistory();
    return state === 'granted';
  };

  /*
    The gate again, but waiting first when the answer is still on its way — and the one place this
    file asks Shopify to load anything.

    `main.js` and this asset are both deferred and the theme never loads the Customer Privacy API
    itself, so on a shop with Shopify's cookie banner the API arrives AFTER both of these elements
    have connected: measured on the stage store 2026-09-17, at the moment `lunya-view-recorder` is
    defined `Shopify.customerPrivacy` is undefined while `Shopify.loadFeatures` is already a
    function. Reading the gate once at connect would therefore treat every such shop as one that
    has not configured consent, and record a view the shopper may be about to refuse.

    The call shape is the one the Customer Privacy API documents: the feature list first, a
    callback SECOND that receives an error or nothing. (`assets/product-model.js` is this repo's
    other `loadFeatures` caller and uses the per-feature `onLoad` form instead — that feature has
    one, this one does not.) An error, a `loadFeatures` that throws, no `loadFeatures` at all, and
    a callback that never fires all leave the caller exactly where it was: the no-op gate above,
    and for the row, the server render.

    The API is a page-level resource, so the load is single-flight: the first caller asks for it and
    every later one queues on the same completion. Two history rows on one page, and a recorder
    whose element reconnects, would otherwise each call `loadFeatures`. Each queued caller still
    runs its OWN gate when the load resolves, because consent is per caller and per moment, not
    something the first asker can answer on everyone's behalf.
  */
  const CONSENT_FEATURE = [{ name: 'consent-tracking-api', version: '0.1' }];
  // null until the first caller needs the API. `{ done, queue }` after that; `done` is also what
  // makes a caller arriving afterwards answer immediately instead of queueing forever.
  let consentLoad = null;

  const whenPersonalisationAllowed = (callback) => {
    const shopify = window.Shopify;
    const gate = () => callback(personalisationAllowed());
    // Only "the API is not here yet, but can be" is worth waiting for. The editor and an API that
    // is already present both have an answer now, and asking for a feature would not change it.
    const loadable = shopify
      && !shopify.designMode
      && !shopify.customerPrivacy
      && typeof shopify.loadFeatures === 'function';
    if (!loadable) {
      gate();
      return;
    }
    if (consentLoad) {
      if (consentLoad.done) gate();
      else consentLoad.queue.push(gate);
      return;
    }

    const load = { done: false, queue: [gate] };
    consentLoad = load;
    // Guarded against a `loadFeatures` that calls back twice, and emptied before the callbacks run
    // so one of them re-entering cannot be handed its own queue.
    const settle = () => {
      if (load.done) return;
      load.done = true;
      const queued = load.queue;
      load.queue = [];
      queued.forEach((run) => run());
    };
    try {
      shopify.loadFeatures(CONSENT_FEATURE, settle);
    } catch (error) {
      settle();
    }
  };

  /*
    Best effort ends here, both times. An abort is routine — `disconnectedCallback` causes it, and
    a shopper who scrolls past is not a problem — but anything else is a bug in this file, and a
    silent catch is how one survives a release. The shopper sees nothing either way: there is a
    complete row on the page already. Same shape as Symmetry's own quick-buy handler
    (`console.warn(error)`, assets/main.js:3157).
  */
  const failed = (error) => {
    if (error && error.name === 'AbortError') return;
    console.warn('lunya-recently-viewed:', error); // eslint-disable-line no-console
  };

  /*
    Every link in ONE incoming card, with the search route's analytics taken back off it.

    Rewritten path-relative so a fetched card's href is the same string a server-rendered card
    carries; every other param and the hash stay. Only same-origin links are touched: `new URL` is
    the step that throws (a malformed `http://[`), and a `javascript:` or off-site href has no
    path to write back and is left exactly as it was.
  */
  const stripSearchTracking = (card) => {
    card.querySelectorAll('a[href]').forEach((anchor) => {
      let url;
      try {
        url = new URL(anchor.getAttribute('href'), window.location.origin);
      } catch (error) {
        return;
      }
      if (url.origin !== window.location.origin) return;
      // Copied first: deleting from a live `URLSearchParams` while iterating it skips entries.
      const tracking = [...url.searchParams.keys()].filter(isTrackingParam);
      if (tracking.length === 0) return;
      tracking.forEach((name) => url.searchParams.delete(name));
      anchor.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    });
  };

  // The cards of one grid, keyed by product id and in the order they were found. First card wins:
  // an id that somehow appears twice must not overwrite the node already placed for it.
  const cardsById = (cards) => {
    const byId = new Map();
    cards.forEach((card) => {
      const id = String(card.dataset.productId || '');
      if (id && !byId.has(id)) byId.set(id, card);
    });
    return byId;
  };

  class LunyaRecentlyViewed extends HTMLElement {
    /*
      Tolerant on purpose: this string was written by some earlier version of the site and is read
      back by this one, and a shopper can edit it by hand. Invalid JSON, a value that is not an
      array, and entries with no usable id are all "no history", not an error — throwing here would
      take the row's enhancement with it. Ids and handles are coerced, because JSON round-trips a
      number as a number while the PDP's dataset only ever yields strings.
    */
    static parse(raw) {
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        return [];
      }
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((entry) => entry != null && typeof entry === 'object' && entry.id != null && entry.id !== '')
        /*
          Capped on the way IN as well as by `push` on the way out: every id read here ends up in
          one search query, and a value written by hand — or by a later version with a bigger cap —
          would otherwise become a URL no server accepts, and quietly cost the row its history.
        */
        .slice(0, HISTORY_CAP)
        .map((entry) => ({
          id: String(entry.id),
          handle: entry.handle == null ? '' : String(entry.handle),
        }));
    }

    /*
      Newest first, one entry per product, capped. Returns a new array rather than splicing the one
      it was given: `read` hands back a list the caller may still be holding, and a reorder that
      happened in place would change what a second consumer sees without anything saying so.
    */
    static push(list, entry, cap = HISTORY_CAP) {
      if (!entry || entry.id == null || entry.id === '') return list;
      const id = String(entry.id);
      // Compared as strings: a product viewed twice must not end up as two cards, and the two
      // writers disagree about the type — `{{ product.id }}` reaches the recorder through a
      // dataset attribute as a string, while the same id read back out of JSON is a number.
      const older = list.filter((item) => String(item.id) !== id);
      return [{ id, handle: entry.handle == null ? '' : String(entry.handle) }, ...older].slice(0, cap);
    }

    /*
      Reaching for `window.localStorage` is itself the step that fails: with cookies blocked,
      Chrome and Safari throw a SecurityError from the accessor, before `getItem` is called, so
      guarding the read alone is too late. Returns null for "no storage here", which every caller
      below treats as an empty history rather than as a reason to stop.
    */
    static storage() {
      try {
        return window.localStorage || null;
      } catch (error) {
        return null;
      }
    }

    /*
      The read, with its outcome attached: `{ ok, list }`.

      "Nothing stored" and "the read failed" are the same empty list to anything that only DISPLAYS
      history, and completely different things to anything about to write one. Without `ok`, a
      transient `getItem` throw on a browser whose `setItem` still works has `record` push onto an
      empty list and replace a real ten-entry history with a single entry — a silent data loss that
      looks exactly like a first visit.

      A value that is present but unparseable is a RESOLVED read: `getItem` answered, what it
      answered with is unusable, and replacing that garbage with a clean list is the point. Only the
      read itself failing — a blocked accessor, a SecurityError — is `ok: false`.
    */
    static loadHistory(storage) {
      if (!storage) return { ok: false, list: [] };
      try {
        return { ok: true, list: LunyaRecentlyViewed.parse(storage.getItem(STORAGE_KEY)) };
      } catch (error) {
        return { ok: false, list: [] };
      }
    }

    // The list, and `[]` for every failure. Unchanged on purpose: the row element only displays
    // what it gets back, and an unreadable store is an unpersonalised row and nothing more.
    static read(storage) {
      return LunyaRecentlyViewed.loadHistory(storage).list;
    }

    /*
      Reports success as a boolean instead of throwing: recording a view is best effort, and
      nothing on the page waits on it. A full quota, a private window and a blocked accessor all
      end up here.
    */
    static record(storage, entry) {
      if (!storage) return false;
      const stored = LunyaRecentlyViewed.loadHistory(storage);
      // The read is what justifies the write. Nothing is written over a history this browser would
      // not show us.
      if (!stored.ok) return false;
      try {
        storage.setItem(STORAGE_KEY, JSON.stringify(LunyaRecentlyViewed.push(stored.list, entry)));
        return true;
      } catch (error) {
        return false;
      }
    }

    /*
      The row's final order, as `{ id, from }` so the caller knows which node each id belongs to.

      History leads, in the order it is stored — most recently viewed first — and not in the order
      the fetch came back in: the row promises "what you were just looking at", while the search
      route answers in its own relevance order. An id the fetch did not return is skipped, since an
      unpublished, deleted or out-of-stock product can sit in a shopper's history for months.
      Whatever is left of the row is filled from the fallback the server rendered, minus anything
      history has already placed, so no product appears twice. Ids are compared as strings for the
      same reason `push` does: the two sides come from JSON and from a dataset attribute.

      `limit` defaults to the storage cap rather than being required: `composed.length >= limit` is
      never true for an undefined one, so a caller that omitted it would be handed every candidate
      and the row would grow past the length the merchant set.
    */
    static compose(historyIds, fetchedIds, fallbackIds, limit = HISTORY_CAP) {
      const fetched = new Set((fetchedIds || []).map(String));
      const used = new Set();
      const composed = [];
      const take = (rawId, from) => {
        const id = String(rawId);
        if (composed.length >= limit || used.has(id)) return;
        used.add(id);
        composed.push({ id, from });
      };

      (historyIds || []).forEach((id) => {
        if (fetched.has(String(id))) take(id, 'history');
      });
      (fallbackIds || []).forEach((id) => take(id, 'fallback'));
      return composed;
    }

    connectedCallback() {
      /*
        Already done, or already under way, and not to be started again. The observer
        `initLazyScript` registers belongs to main.js and cannot be cancelled from here, so a row
        that is disconnected and reconnected — a theme-editor section reload, a DOM move — would
        accumulate one per reconnect. Not asking is the only way to not have them.

        THREE states, because "we have asked for consent" and "an observer exists" are different
        facts and only one of them is worth keeping across a detach:

        - `awaitingConsent` — a consent wait is outstanding. It stops a reconnect starting a second
          wait, and is cleared the moment the callback runs, whether or not that callback registered
          anything. That clearing IS the recovery: a callback firing while the element is detached
          registers no observer, so a reconnect has to be able to enter the wait again. Latching
          this state would leave the row inert for the rest of its life, with no stale observer to
          fall back on.
        - `observing` — an observer has actually been registered. Cleared only by
          `disconnectedCallback`, and only when an abort cost the row its fetch: clearing it on
          every disconnect would register a second observer in the common case.
        - `personalised` — the request has gone out.
      */
      if (this.personalised || this.observing || this.awaitingConsent) return;
      // The theme editor: nothing at all, whatever mode the row is in. Kept apart from consent,
      // because the two stopped meaning the same thing once a row could fill from related products.
      if (window.Shopify && window.Shopify.designMode) return;
      /*
        On a product page the row is told which product it sits on (`data-exclude-id`): the recorder
        on this very page has just put that product first in the history, and "you may also like
        the thing you are looking at" is not a recommendation. It may also be given somewhere to
        fill from when the history runs out (`data-related-url`, Shopify's related products) instead
        of rendered cards — the product page's You May Also Like, which must not fall back to the
        same Best Sellers the row under it already shows.
      */
      const excludeId = this.dataset.excludeId ? String(this.dataset.excludeId) : null;
      const relatedUrl = this.dataset.relatedUrl || null;
      /*
        A refusal costs the HISTORY, not the row (bot review of PR #30, 2026-09-18). The history is
        the personal part; Shopify's related products are not, and a shopper who declined
        preferences consent was getting no You May Also Like row at all. So a refusing shopper has
        an empty history — and a collection row, which has nothing else to show, still stops below.
        `let`, because the answer can change before the cards are asked for and again before they
        are inserted; it is re-read at both points.
      */
      let history = personalisationAllowed()
        ? LunyaRecentlyViewed.read(LunyaRecentlyViewed.storage())
          .map((entry) => entry.id)
          .filter((id) => id !== excludeId)
        : [];
      // The overwhelmingly common case on a first visit to the homepage, and the one worth being
      // cheap about: no request, no parse, and the collection the merchant chose is what the row
      // stays. A row with related products to fall back on still has something to fetch.
      if (history.length === 0 && !relatedUrl) return;
      /*
        How many cards the row holds — and it must not hold more after the swap than before it. The
        rendered cards are the ceiling, not just the fallback: a collection with fewer products than
        the merchant's `products_to_show` renders a short row, and filling it out to `data-limit`
        would add a whole grid row at the 2-up breakpoint.

        `data-limit` therefore only ever shortens, and as the server never renders more cards than
        `products_to_show` it never actually does. Kept as belt and braces: the rendered count is
        read from the DOM, and one attribute is a cheap second opinion about how long the merchant
        meant the row to be.

        Counted from the row's own `product-block` children rather than `children.length`, so
        anything else a future row wraps in here cannot inflate the count.
      */
      const renderedCount = this.querySelectorAll(ROW_CARDS).length;
      const declared = Number(this.dataset.limit);
      // A related-mode row renders nothing (the recommendations object only exists on its own
      // route), so there is no rendered count to be a ceiling: the declared size is the row.
      const limit = renderedCount === 0 && relatedUrl
        ? (declared > 0 ? declared : HISTORY_CAP)
        : (declared > 0 ? Math.min(declared, renderedCount) : renderedCount);

      const personalise = () => {
        /*
          The gate again, and FIRST — before any one-shot state moves. `initLazyScript`'s callback
          can be a whole scroll after connect, long enough for the consent API to have arrived and
          been refused, and that refusal has to cost zero requests.

          Reading it before `personalised` is flipped leaves no latched state behind on the refusal
          path. That is a guard, not a shopper flow: `initLazyScript` unobserves after its single
          hit and `observing` stops the element registering another, so in production nothing calls
          this a second time. It means a future second caller — or a reconnect that legitimately
          re-enters — is not silently dead because a refusal spent the one-shot.

          And it hands `observing` back. Symmetry's observer unobserves after this single hit, so
          after a refusal there IS no observer, and a flag that still said so made every later
          reconnect return early for the rest of the element's life. `observing` has to mean "an
          observer exists", not "one once did".
        */
        if (!personalisationAllowed()) {
          // Nothing else to show: a collection row stops here, and hands the observer slot back.
          if (!relatedUrl) {
            this.observing = false;
            return;
          }
          // Related mode: whatever was read at connect is no longer ours to use.
          history = [];
        }
        /*
          `personalised` is the one-shot here as well as at the top: a row that reconnects BEFORE it
          ever personalised gets past that early return legitimately, and can end up with two
          observers that both fire. `isConnected` covers the other end — a row removed between the
          observer firing and this running.
        */
        if (this.personalised || !this.isConnected) return;
        this.personalised = true;
        /*
          The `try` is for the synchronous half of the same failure. `new URL` throws before there
          is a promise to reject — on a `file://` page or inside a sandboxed iframe,
          `location.origin` is the string "null" and there is no base to resolve against — and this
          runs inside `initLazyScript`'s IntersectionObserver callback, where nothing else is
          catching. Every way this can fail has to end in the same place.
        */
        try {
          const request = new AbortController();
          this.request = request;
          // Each route fails on its own: a search outage must not cost the row its related
          // products, nor the other way round. `failed` still reports whatever was not an abort.
          const soft = (error) => {
            failed(error);
            return new Map();
          };
          const nothing = Promise.resolve(new Map());
          const viewed = history.length > 0
            ? this.fetchCards(this.historyUrl(history), request.signal).catch(soft)
            : nothing;
          const related = relatedUrl
            ? this.fetchCards(new URL(relatedUrl, window.location.origin), request.signal).catch(soft)
            : nothing;
          Promise.all([viewed, related])
            .then(([fetched, relatedCards]) => this.swapIn(history, fetched, limit, relatedCards, excludeId))
            .catch(failed)
            /*
              No longer in flight, whichever way it went. `disconnectedCallback` reads
              `this.request` to tell an abort that cost the row its fetch from a disconnect long
              after one completed, and a controller left behind here would make every later
              disconnect look like the first. Compared by identity, so a chain settling late
              cannot clear a newer request's controller.
            */
            .finally(() => {
              if (this.request === request) this.request = null;
            });
        } catch (error) {
          failed(error);
        }
      };

      /*
        Marked before the consent wait rather than after it: the wait is asynchronous, and a
        disconnect and reconnect while it is outstanding would otherwise start a second one.
      */
      this.awaitingConsent = true;
      whenPersonalisationAllowed((allowed) => {
        // The wait is over either way, so the row is eligible to ask again — see the three states
        // above. Cleared before the guard, not after it.
        this.awaitingConsent = false;
        // The wait can outlive the element, and a callback arriving after that must do nothing.
        if ((!allowed && !relatedUrl) || !this.isConnected || this.personalised) return;
        if (!allowed) history = [];
        this.observing = true;
        /*
          The row is below the fold on the homepage, so the request waits for Symmetry's own
          viewport helper (assets/main.js:7) — the same deferral the theme's fetched sections use.
          Called directly when main.js has not loaded: the enhancement is worth more than the
          deferral, and `initLazyScript` falls back to an immediate call itself where there is no
          IntersectionObserver.
        */
        /*
          What to watch. A row still waiting for its cards is rendered `hidden`, and an element with
          `display: none` has no box — an IntersectionObserver never reports it, so observing the
          row itself meant it never fetched at all (found live on stage, 2026-09-17). Its Shopify
          section wrapper is a zero-height box in exactly the right place, and a zero-area target
          that is edge-adjacent to the root does intersect.
        */
        const pending = typeof this.closest === 'function' ? this.closest('[data-lunya-row-pending]') : null;
        const target = (pending && this.closest('.shopify-section')) || this;
        if (typeof window.initLazyScript === 'function') {
          window.initLazyScript(target, personalise, LAZY_THRESHOLD);
        } else personalise();
      });
    }

    /*
      Consent was withdrawn with the page open. A collection row goes back to exactly what the
      server rendered; a related row, which had nothing rendered, just loses its history cards and
      keeps the related ones. A row that never personalised has nothing to undo.
    */
    forget() {
      if (!this.historyShown || this.historyShown.length === 0) return;
      const shown = this.historyShown;
      this.historyShown = [];
      if (this.renderedCards && this.renderedCards.length > 0) {
        this.replaceChildren(...this.renderedCards);
        return;
      }
      Array.from(this.querySelectorAll(ROW_CARDS))
        .filter((card) => shown.includes(String(card.dataset.productId)))
        .forEach((card) => card.remove());
      // History may have been all a related row had. It was revealed by swapIn, so put it back the
      // way it started: a heading over an empty grid is worse than no row.
      if (this.querySelectorAll(ROW_CARDS).length === 0) {
        const pending = typeof this.closest === 'function' ? this.closest('[data-lunya-row-pending]') : null;
        if (pending) pending.setAttribute('hidden', '');
      }
    }

    disconnectedCallback() {
      if (!this.request) return;
      this.request.abort();
      this.request = null;
      /*
        The abort just cost the row a fetch that had swapped nothing, and both one-shots were set
        before it went out — so without this a row disconnected mid-flight and reconnected (a
        theme-editor reorder, a DOM move) would never personalise at all, having already spent its
        one chance on a request nobody received. Hand them back: the reconnect registers exactly
        one new observer and issues exactly one new request.

        A row whose fetch has already settled has no controller left here to abort (`fetchCards`
        clears it either way), so a row that already swapped stays done. That conditionality is the
        whole point: clearing `observing` on EVERY disconnect would register a second observer for
        every reconnect of an already-personalised row.

        `awaitingConsent` is not touched here. A request in flight means the wait already resolved,
        so it is false; and a detach DURING the wait is recovered by the callback clearing it
        itself, which is the one case this early return cannot see.
      */
      this.personalised = false;
      this.observing = false;
    }

    /*
      One request for the whole row, and the only place this element touches the network.

      `q` is a search for the stored ids themselves — `id:123 OR id:456` — which is how the search
      route can be asked for a specific set of products. Built through `URLSearchParams` rather than
      by concatenation: the ids come out of localStorage, which the shopper can edit by hand, and
      the encoding is also what keeps the `:` and the spaces intact (verified against the stage
      store).

      ALL of them, not the first `limit`: unpublished, deleted and search-hidden products come back
      as a 200 with no card for them, and the whole point of storing ten is that the row can still
      lead with history when some of them have gone. `compose` is what truncates the row, and the
      fetched section pages by ten, which is the cap.
    */
    historyUrl(historyIds) {
      const url = new URL(this.dataset.searchUrl, window.location.origin);
      url.search = new URLSearchParams({
        section_id: CARDS_SECTION,
        type: 'product',
        q: historyIds.map((id) => `id:${id}`).join(' OR '),
      }).toString();
      return url;
    }

    // One headless section, two routes: the search route for the history, Shopify's recommendations
    // route for a product page's related products. Same parse, same cards.
    fetchCards(url, signal) {
      return fetch(url.toString(), { signal })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
          return response.text();
        })
        /*
          Parsed into a document of its own, so nothing is evaluated and nothing is loaded until a
          card is inserted. `DOMParser` rather than a `<template>` and `innerHTML`, because this
          file must never assign HTML from a string at all: the one thing it holds is
          shopper-editable storage, and a single innerHTML anywhere invites the next change to
          reach for it with that. Elements parsed here are upgraded when they are inserted into
          this document, exactly as `<template>` content is.
        */
        .then((html) => cardsById(
          new DOMParser().parseFromString(html, 'text/html').querySelectorAll(FETCHED_CARDS),
        ));
    }

    /*
      The swap, and the only place this element writes to the DOM. Four reasons to leave the row
      exactly as it is, all of them cheaper than the alternative:

      - nothing came back, so there is nothing to lead with;
      - the composed order IS the order on screen, which is what a shopper whose most recent view is
        already the collection's first card gets;
      - focus is inside the row — `replaceChildren` would drop it to the document mid-keyboard-use;
      - a quick buy is open, and its panel is a child of the card it belongs to.
    */
    swapIn(historyIds, fetched, limit, related = new Map(), excludeId = null) {
      /*
        The row can have left the document while the response was in flight: `disconnectedCallback`
        aborts the request, but an abort that lands after the body has been read does not
        unschedule a `.then` already queued, so this is the check that holds. Nothing that follows
        would be visible to anyone.
      */
      /*
        Consent can be withdrawn while the requests are in flight. The withdrawal deletes the
        storage, but the history cards were already on their way: read the gate once more, and drop
        them. The related products stay — they were never the personal part.
      */
      if (!personalisationAllowed()) {
        historyIds = [];
        fetched = new Map();
      }
      if (this.dataset.excludeGiftCards === 'true') {
        fetched = new Map(Array.from(fetched).filter(([, card]) => card.dataset.giftCard !== 'true'));
        related = new Map(Array.from(related).filter(([, card]) => card.dataset.giftCard !== 'true'));
      }
      if (!this.isConnected || (fetched.size === 0 && related.size === 0)) return;

      const rendered = cardsById(this.querySelectorAll(ROW_CARDS));
      const current = Array.from(rendered.keys());
      // The fallback pool: what the server rendered, then the related products — never the product
      // this page is about, which Shopify leaves out of its own answer but a rendered row might not.
      const fallback = current.concat(Array.from(related.keys())).filter((id) => id !== excludeId);
      const composed = LunyaRecentlyViewed.compose(historyIds, Array.from(fetched.keys()), fallback, limit);
      if (composed.length === 0) return;
      if (composed.map((entry) => entry.id).join(',') === current.join(',')) return;
      if (this.contains(document.activeElement) || this.querySelector(OPEN_QUICK_BUY)) return;

      // Read off a card that is already in the row, so the incoming ones are pinned to the height
      // the page is currently laid out against.
      const source = this.querySelector(CARD_IMAGE_RATIO);
      let ratio = source ? source.style.getPropertyValue(ASPECT_RATIO) : '';

      const nodes = composed.map(({ id, from }) => {
        // Already in the row: `replaceChildren` MOVES it rather than copying, so its event
        // listeners, its upgraded custom elements and any quick-buy content stay with it.
        if (from === 'fallback' && rendered.has(id)) return rendered.get(id);
        const card = from === 'fallback' ? related.get(id) : fetched.get(id);
        // A row with nothing rendered has no height to protect, but its cards come from two renders
        // that each picked their own ratio: the first incoming card sets it for the rest.
        if (!ratio) {
          const first = card.querySelectorAll(CARD_IMAGE_RATIO)[0];
          ratio = first && first.style.getPropertyValue ? first.style.getPropertyValue(ASPECT_RATIO) : '';
        }
        // Incoming cards only: a rendered card's links were never stamped in the first place.
        stripSearchTracking(card);
        if (ratio) {
          card.querySelectorAll(CARD_IMAGE_RATIO).forEach((image) => {
            image.style.setProperty(ASPECT_RATIO, ratio);
          });
        }
        return card;
      });

      /*
        What this swap is about to take off the page, kept once: a withdrawal of consent while the
        page is open has to take the shopper's history back OFF the screen, not only out of storage.
      */
      if (!this.renderedCards) this.renderedCards = Array.from(rendered.values());
      this.historyShown = composed.filter((entry) => entry.from === 'history').map((entry) => entry.id);
      this.replaceChildren(...nodes);
      // A related-mode row is rendered hidden — a heading over an empty grid is worse than no row —
      // and only a row that now has cards is shown.
      const pending = typeof this.closest === 'function' ? this.closest('[data-lunya-row-pending]') : null;
      if (pending) pending.removeAttribute('hidden');
    }
  }

  customElements.define('lunya-recently-viewed', LunyaRecentlyViewed);

  /*
    A withdrawal made while this page is open. Shopify dispatches `visitorConsentCollected` on the
    document whenever the shopper saves a consent choice; re-reading the gate is enough, because a
    refusal is what deletes. Once per page: the file-level guard above stops a second evaluation.
  */
  if (typeof document !== 'undefined' && document && typeof document.addEventListener === 'function') {
    document.addEventListener('visitorConsentCollected', () => {
      // `personalisationAllowed` deletes the storage on a refusal; the rows take it off the screen.
      if (personalisationAllowed() || consentState() !== 'refused') return;
      if (typeof document.querySelectorAll !== 'function') return;
      document.querySelectorAll('lunya-recently-viewed').forEach((row) => {
        if (typeof row.forget === 'function') row.forget();
      });
    });
  }

  /*
    Its own guard, for the same reason the file has one: this element is rendered on the PDP, whose
    scripts quick buy re-injects into whatever page a card was opened on.
  */
  if (!customElements.get('lunya-view-recorder')) {
    class LunyaViewRecorder extends HTMLElement {
      connectedCallback() {
        whenPersonalisationAllowed((allowed) => {
          /*
            The wait is asynchronous on a shop whose consent API has not loaded yet, so this can
            arrive after the recorder left the document. A view is recorded for the PDP the shopper
            is on, and by then that is no longer this page.
          */
          if (!allowed || !this.isConnected) return;
          // The whole element. Nothing reads the result: a full quota or a private window means
          // this visit is not remembered, which is not something the PDP needs to know about.
          LunyaRecentlyViewed.record(LunyaRecentlyViewed.storage(), {
            id: this.dataset.productId,
            handle: this.dataset.productHandle,
          });
        });
      }
    }

    customElements.define('lunya-view-recorder', LunyaViewRecorder);
  }
})();
